import { Box, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import PropTypes from 'prop-types'
import React from 'react'

export const useStyles = makeStyles(({ spacing, palette, typography }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1.5),
    flexShrink: 0
  },
  dotsContainer: {
    display: 'flex',
    gap: spacing(1.5),
    alignItems: 'center'
  },
  dot: {
    width: 24,
    height: 8,
    borderRadius: 100,
    backgroundColor: palette.grey[300],
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: palette.grey[400]
    }
  },
  dotActive: {
    width: 40,
    height: 8,
    backgroundColor: palette.primary.main,
    '&:hover': {
      backgroundColor: palette.primary.dark || palette.primary.main
    }
  },
  label: {
    ...typography.body.s,
    color: palette.text.secondary,
    fontWeight: typography.weight.medium
  }
}))

/**
 * Stepper - A pill-shaped dots indicator for multi-step processes
 * Displays progress through steps with clickable navigation dots
 *
 * @param {number} currentStep - Current active step (0-indexed)
 * @param {number} totalSteps - Total number of steps
 * @param {function} onStepClick - Callback when a step dot is clicked (optional)
 * @param {boolean} showLabel - Whether to show "Step X of Y" label (default: true)
 * @param {string} labelPrefix - Custom prefix for label (default: "Step")
 */
const Stepper = ({ currentStep, totalSteps, onStepClick, showLabel = true, labelPrefix = 'Step', className, ...other }) => {
  const classes = useStyles()

  const handleStepClick = (index) => {
    if (onStepClick && index !== currentStep) {
      onStepClick(index)
    }
  }

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleStepClick(index)
    }
  }

  return (
    <Box className={`${classes.root} ${className || ''}`} {...other}>
      <Box className={classes.dotsContainer}>
        {Array.from({ length: totalSteps }).map((_, index) => (
          <Box
            key={index}
            className={`${classes.dot} ${index === currentStep ? classes.dotActive : ''}`}
            onClick={() => handleStepClick(index)}
            role='button'
            tabIndex={onStepClick ? 0 : -1}
            onKeyDown={(e) => handleKeyDown(e, index)}
            aria-label={`Go to ${labelPrefix.toLowerCase()} ${index + 1}`}
            aria-current={index === currentStep ? 'step' : undefined}
          />
        ))}
      </Box>
      {showLabel && (
        <Typography className={classes.label}>
          {labelPrefix} {currentStep + 1} of {totalSteps}
        </Typography>
      )}
    </Box>
  )
}

Stepper.propTypes = {
  /** Current active step (0-indexed) */
  currentStep: PropTypes.number.isRequired,
  /** Total number of steps */
  totalSteps: PropTypes.number.isRequired,
  /** Callback function when a step dot is clicked. Receives the step index as argument */
  onStepClick: PropTypes.func,
  /** Whether to show the "Step X of Y" label */
  showLabel: PropTypes.bool,
  /** Custom prefix for the label (e.g., "Step", "Page", "Slide") */
  labelPrefix: PropTypes.string,
  /** Additional CSS classes */
  className: PropTypes.string
}

export default Stepper
